import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EmotoradOus,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {getOptionalEventContainerOrganizationalUnitId} from "~/utilities/ottopilotSemanticColumns";

function findOrganizationalUnitByPossibleId(
    organizationalUnits: ReadonlyArray<OrganizationalUnit>,
    value: unknown,
): OrganizationalUnit | undefined {
    if (typeof value !== "string" || value.trim() === "") {
        return undefined;
    }
    return organizationalUnits.find((unit) => unit.id === value);
}

function getLifecycleDealerFieldByLabel(
    organizationalUnits: ReadonlyArray<OrganizationalUnit>,
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>,
    dealerId: Uuid | null,
    labels: ReadonlyArray<string>,
): string {
    if (dealerId === null) {
        return "";
    }
    const dealerOu = organizationalUnits.find((unit) => unit.id === dealerId);
    if (dealerOu === undefined) {
        return "";
    }
    const template = organizationalUnitTemplates.find((item) => item.id === dealerOu.templateId);
    if (template === undefined) {
        return "";
    }
    for (const label of labels) {
        const matchingColumn = template.additionalColumns.find((column) => {
            return column.displayName.trim().toLowerCase() === label.toLowerCase();
        });
        if (matchingColumn === undefined) {
            continue;
        }
        const rawValue = dealerOu.additionalColumnValues[matchingColumn.id];
        if (rawValue !== null && rawValue !== undefined && String(rawValue).trim() !== "") {
            return String(rawValue);
        }
    }
    return "";
}

/**
 * Same as Sales / Lifecycle Overview table: zone lives on an ancestor OU with Zone template (Name column),
 * not only on the dealership leaf.
 */
function resolveLifecycleAssignedDealerZoneFromOuAncestorTree(
    organizationalUnits: ReadonlyArray<OrganizationalUnit>,
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>,
    dealerOuId: Uuid | null,
): string {
    const zoneTemplateId = EmotoradOus.Zone as Uuid;
    const zoneColumnLabels = ["Zone", "Dealer Zone", "Zone Name"] as const;

    const ouNameFromNameColumn = (ou: OrganizationalUnit): string => {
        const template = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
        if (template === undefined) {
            return "";
        }
        const nameColumn = template.additionalColumns.find(
            (col) => col.displayName.trim().toLowerCase() === "name",
        );
        if (nameColumn === undefined) {
            return "";
        }
        const nameValue = ou.additionalColumnValues[nameColumn.id];
        return typeof nameValue === "string" && nameValue.trim() !== "" ? nameValue.trim() : "";
    };

    const zoneColumnValueOnOu = (ou: OrganizationalUnit): string | null => {
        const template = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
        if (template === undefined) {
            return null;
        }
        for (const label of zoneColumnLabels) {
            const column = template.additionalColumns.find(
                (c) => c.displayName.trim().toLowerCase() === label.toLowerCase(),
            );
            if (column === undefined) {
                continue;
            }
            const value = ou.additionalColumnValues[column.id];
            if (value !== null && value !== undefined && String(value).trim() !== "") {
                return String(value).trim();
            }
        }
        return null;
    };

    if (dealerOuId === null) {
        return "";
    }
    const visited = new Set<Uuid>();
    let current: OrganizationalUnit | undefined = organizationalUnits.find((unit) => unit.id === dealerOuId);
    for (;;) {
        if (current === undefined) {
            break;
        }
        if (visited.has(current.id) === true) {
            break;
        }
        const ou = current;
        visited.add(ou.id);
        if (ou.templateId === zoneTemplateId) {
            const zoneName = ouNameFromNameColumn(ou);
            if (zoneName !== "") {
                return zoneName;
            }
        }
        current =
            ou.parentId !== null
                ? organizationalUnits.find((unit) => unit.id === ou.parentId)
                : undefined;
    }
    visited.clear();
    current = organizationalUnits.find((unit) => unit.id === dealerOuId);
    for (;;) {
        if (current === undefined) {
            break;
        }
        if (visited.has(current.id) === true) {
            break;
        }
        const ou = current;
        visited.add(ou.id);
        const fromColumn = zoneColumnValueOnOu(ou);
        if (fromColumn !== null) {
            return fromColumn;
        }
        current =
            ou.parentId !== null
                ? organizationalUnits.find((unit) => unit.id === ou.parentId)
                : undefined;
    }
    return "";
}

function resolveLifecycleDealerOuId(
    journey: EventContainerWithEvents,
    eventContainerTemplate: EventContainerTemplate,
    organizationalUnits: ReadonlyArray<OrganizationalUnit>,
): Uuid | null {
    const assignedDealerRaw = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID];
    const assignedDealerOu =
        findOrganizationalUnitByPossibleId(organizationalUnits, assignedDealerRaw)
        ?? findOrganizationalUnitByPossibleId(
            organizationalUnits,
            getOptionalEventContainerOrganizationalUnitId(journey, eventContainerTemplate),
        )
        ?? findOrganizationalUnitByPossibleId(
            organizationalUnits,
            journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID],
        )
        ?? findOrganizationalUnitByPossibleId(
            organizationalUnits,
            journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID],
        )
        ?? findOrganizationalUnitByPossibleId(
            organizationalUnits,
            journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID],
        );
    return (
        assignedDealerOu?.id
        ?? (typeof assignedDealerRaw === "string" && assignedDealerRaw.trim() !== ""
            ? (assignedDealerRaw as Uuid)
            : null)
    );
}

/** Resolve assigned dealer city/state/zone for Lifecycle Overview (and matching view panels). */
export function resolveEmotoradLifecycleDealerCityStateZone(
    journey: EventContainerWithEvents,
    eventContainerTemplate: EventContainerTemplate,
    kind: "city" | "state" | "zone",
    organizationalUnits: ReadonlyArray<OrganizationalUnit>,
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>,
): string {
    const dealerId = resolveLifecycleDealerOuId(journey, eventContainerTemplate, organizationalUnits);
    if (kind === "zone") {
        const fromAncestorTree = resolveLifecycleAssignedDealerZoneFromOuAncestorTree(
            organizationalUnits,
            organizationalUnitTemplates,
            dealerId,
        );
        if (fromAncestorTree !== "") {
            return fromAncestorTree;
        }
    }
    const fromOu =
        kind === "city"
            ? getLifecycleDealerFieldByLabel(organizationalUnits, organizationalUnitTemplates, dealerId, [
                  "City",
                  "Dealer City",
              ])
            : kind === "state"
              ? getLifecycleDealerFieldByLabel(organizationalUnits, organizationalUnitTemplates, dealerId, [
                    "State",
                    "Dealer State",
                ])
              : getLifecycleDealerFieldByLabel(organizationalUnits, organizationalUnitTemplates, dealerId, [
                    "Zone",
                    "Dealer Zone",
                    "Zone Name",
                ]);
    if (fromOu !== "") {
        return fromOu;
    }
    const columnId =
        kind === "city"
            ? EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID
            : kind === "state"
              ? EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID
              : EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID;
    const raw = journey.additionalColumnValues[columnId];
    if (raw === undefined || raw === null || String(raw).trim() === "") {
        return "-";
    }
    return String(raw);
}

export const EMOTORAD_LIFECYCLE_DEALER_GEOGRAPHY_COLUMN_IDS = [
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
] as const;

export function isEmotoradLifecycleDealerGeographyColumnId(columnId: string | Uuid): boolean {
    return EMOTORAD_LIFECYCLE_DEALER_GEOGRAPHY_COLUMN_IDS.some((id) => id === columnId);
}
